/**
 * ACT-1293: CSV bulk user import modal.
 *
 * CSV format:
 *   Header row required: email,displayName,roleCodes,personaCodes
 *   - email        required; must be non-empty and unique within the file
 *   - displayName  optional free-text
 *   - roleCodes    optional; semicolon-delimited list  e.g.  admin;clinical
 *                  (valid: admin, clinical, readonly)
 *   - personaCodes optional; semicolon-delimited list  e.g.  CARE_MANAGER;PROVIDER
 *                  (valid: PROVIDER, CUSTOMER_SERVICE, MSO, CARE_MANAGER,
 *                   ADMIN, MEDICAL_ASSISTANT)
 *
 * Excel exports: UTF-8 BOM is stripped from the first header cell automatically.
 *
 * Chunking: if the CSV has >500 valid rows the mutation is called in sequential
 * ≤500-row batches; results accumulate across batches.
 *
 * Partial-batch failure: if a batch call throws (network/GQL error, not per-row),
 * any already-completed batches are preserved in the results view with a banner
 * naming the failed batch. The modal never reverts to preview after a batch succeeds.
 *
 * Per-row result view: after submit shows ✓/✗ per email with the error field.
 */

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import Papa from "papaparse";
import { useMutation } from "@apollo/client";
import { Upload, X, Download, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

import {
  CREATE_USERS_MUTATION,
  type CreateUsersVars,
  type CreateUsersResult,
  type CreateUserInput,
  type CreateUsersRowResult,
} from "../graphql/adminOps";
import { PERSONAS, KNOWN_ROLE_CODES } from "../../config/roles";

// ── Constants ────────────────────────────────────────────────────────────────

const BATCH_SIZE = 500;
const LIST_DELIMITER = ";";

/** Valid persona codes (uppercase) — authoritative FE enum (roles.ts PERSONAS). */
const KNOWN_PERSONA_CODES: ReadonlySet<string> = new Set(Object.values(PERSONAS));

/** Defense-in-depth field caps applied before submit (BE remains the gate). */
const MAX_DISPLAY_NAME_LEN = 200;
const MAX_EMAIL_LEN = 320; // RFC 5321 max email length

const TEMPLATE_CSV =
  `email,displayName,roleCodes,personaCodes\n` +
  `jane.smith@example.com,Jane Smith,admin,CARE_MANAGER;PROVIDER\n` +
  `bob.jones@example.com,Bob Jones,clinical,CARE_MANAGER\n`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ParsedRow {
  /** 1-based line number in the original CSV (header = line 1) */
  lineNumber: number;
  email: string;
  displayName: string;
  roleCodes: string[];
  personaCodes: string[];
  /** Validation error if non-null; row will NOT be submitted */
  validationError: string | null;
}

type ModalPhase = "idle" | "preview" | "submitting" | "results";

// ── Helpers ───────────────────────────────────────────────────────────────────

function splitList(raw: string): string[] {
  return raw
    .split(LIST_DELIMITER)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Parse raw PapaParse rows into validated ParsedRow objects.
 *
 * Validates: email present + syntactically valid + within length; roleCodes /
 * personaCodes against the known enums (roles.ts); displayName length; duplicate
 * emails within file.
 *
 * Email normalization (Fix: email-case mismatch): the email stored on the row is
 * the NORMALIZED form (lowercased + trimmed). It is used for the dedup key, the
 * preview, AND the submitted payload — so what the preview shows as "ok" is
 * exactly what is sent, with no case drift between preview and submission.
 */
export function parseRawRows(rawRows: Record<string, string>[]): ParsedRow[] {
  // First pass: basic per-row validation
  const rows: ParsedRow[] = rawRows.map((raw, idx) => {
    const lineNumber = idx + 2; // +1 for 0-index, +1 for header row
    // Normalize email once: lowercase + trim. This SAME value is deduped,
    // previewed, and submitted (no original-case payload divergence).
    const email = (raw["email"] ?? "").trim().toLowerCase();
    const displayName = (raw["displayName"] ?? "").trim();
    const roleCodes = splitList(raw["roleCodes"] ?? "");
    const personaCodes = splitList(raw["personaCodes"] ?? "");

    let validationError: string | null = null;
    if (!email) {
      validationError = "email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      validationError = "invalid email format";
    } else if (email.length > MAX_EMAIL_LEN) {
      validationError = `email exceeds ${MAX_EMAIL_LEN} characters`;
    } else if (displayName.length > MAX_DISPLAY_NAME_LEN) {
      validationError = `displayName exceeds ${MAX_DISPLAY_NAME_LEN} characters`;
    } else {
      const badRoles = roleCodes.filter((c) => !KNOWN_ROLE_CODES.has(c));
      const badPersonas = personaCodes.filter((c) => !KNOWN_PERSONA_CODES.has(c));
      if (badRoles.length > 0) {
        validationError = `unknown role code(s): ${badRoles.join(", ")}`;
      } else if (badPersonas.length > 0) {
        validationError = `unknown persona code(s): ${badPersonas.join(", ")}`;
      }
    }

    return { lineNumber, email, displayName, roleCodes, personaCodes, validationError };
  });

  // Second pass: flag duplicate emails (keep first occurrence valid, mark rest).
  // email is already normalized (lowercased) above, so the key matches the
  // submitted value exactly.
  const seen = new Set<string>();
  for (const row of rows) {
    if (row.validationError !== null || !row.email) continue;
    if (seen.has(row.email)) {
      row.validationError = "duplicate email in file";
    } else {
      seen.add(row.email);
    }
  }

  return rows;
}

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "user-import-template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PreviewTable({ rows }: { rows: ParsedRow[] }) {
  const validCount = rows.filter((r) => !r.validationError).length;
  const invalidCount = rows.filter((r) => r.validationError).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">
          <strong className="text-foreground">{rows.length}</strong> rows parsed
        </span>
        {validCount > 0 && (
          <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
            {validCount} valid
          </span>
        )}
        {invalidCount > 0 && (
          <span className="inline-flex items-center gap-1 text-destructive">
            <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
            {invalidCount} invalid
          </span>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-auto max-h-64">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground w-8">#</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Email</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Display Name</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Roles</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Personas</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.lineNumber}
                className={
                  "border-b border-border last:border-0 " +
                  (row.validationError
                    ? "bg-destructive/5 dark:bg-destructive/10"
                    : "hover:bg-muted/20")
                }
              >
                <td className="px-3 py-2 text-muted-foreground">{row.lineNumber}</td>
                <td className="px-3 py-2 font-medium">
                  {row.email || <span className="text-muted-foreground italic">—</span>}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{row.displayName || "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{row.roleCodes.join(", ") || "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{row.personaCodes.join(", ") || "—"}</td>
                <td className="px-3 py-2">
                  {row.validationError ? (
                    <span className="text-destructive inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" />
                      {row.validationError}
                    </span>
                  ) : (
                    <span className="text-green-700 dark:text-green-400">
                      <CheckCircle2 className="w-3 h-3 inline mr-0.5" aria-hidden="true" />
                      ok
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultsTable({
  results,
  batchError,
}: {
  results: CreateUsersRowResult[];
  batchError: string | null;
}) {
  const successCount = results.filter((r) => r.success).length;
  const failCount = results.filter((r) => !r.success).length;

  return (
    <div className="flex flex-col gap-3">
      {/* Partial-batch failure banner */}
      {batchError && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{batchError} — rows below reflect completed batches only.</span>
        </div>
      )}

      <div className="flex items-center gap-4 text-sm">
        <span className="inline-flex items-center gap-1.5 text-green-700 dark:text-green-400 font-medium">
          <CheckCircle2 className="w-4 h-4" aria-hidden="true" />
          {successCount} created
        </span>
        {failCount > 0 && (
          <span className="inline-flex items-center gap-1.5 text-destructive font-medium">
            <XCircle className="w-4 h-4" aria-hidden="true" />
            {failCount} failed
          </span>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-auto max-h-72">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Email</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Result</th>
              <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Error</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row, idx) => (
              <tr
                key={`${row.email}-${idx}`}
                className={
                  "border-b border-border last:border-0 " +
                  (row.success
                    ? "hover:bg-muted/20"
                    : "bg-destructive/5 dark:bg-destructive/10")
                }
              >
                <td className="px-3 py-2 font-medium">{row.email}</td>
                <td className="px-3 py-2">
                  {row.success ? (
                    <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                      <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                      Created
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-destructive">
                      <XCircle className="w-3 h-3" aria-hidden="true" />
                      Failed
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {row.error ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

interface CsvUploadModalProps {
  onClose: () => void;
  onImported: () => void;
}

export default function CsvUploadModal({ onClose, onImported }: CsvUploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<ModalPhase>("idle");
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [allResults, setAllResults] = useState<CreateUsersRowResult[]>([]);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  // batchError: set when a mid-run batch throw occurs; results phase still shown
  const [batchError, setBatchError] = useState<string | null>(null);
  // rowCountWarning: set when BE returns a different number of results than inputs sent
  const [rowCountWarning, setRowCountWarning] = useState<string | null>(null);

  const [createUsers] = useMutation<CreateUsersResult, CreateUsersVars>(
    CREATE_USERS_MUTATION,
  );

  // ── File pick & parse ───────────────────────────────────────────────────

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    setParsedRows([]);
    setPhase("idle");

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      // Fix 2: strip leading BOM (U+FEFF) that Excel adds to UTF-8 exports,
      // then trim whitespace. Without this the first header becomes "﻿email"
      // and the email-column check fails.
      transformHeader: (h: string) => h.replace(/^﻿/, "").trim(),
      complete: (results: Papa.ParseResult<Record<string, string>>) => {
        const rawRows = results.data;

        // Validate that the expected header exists
        const meta = results.meta.fields ?? [];
        if (!meta.includes("email")) {
          setParseError(
            'CSV is missing the "email" header column. ' +
            'Expected headers: email, displayName, roleCodes, personaCodes',
          );
          return;
        }

        if (rawRows.length === 0) {
          setParseError("CSV has no data rows (only a header was found).");
          return;
        }

        // Fix 3: duplicate detection happens inside parseRawRows
        const parsed = parseRawRows(rawRows);
        setParsedRows(parsed);
        setPhase("preview");
      },
      error: (err: Error) => {
        setParseError(`Failed to parse CSV: ${err.message}`);
      },
    });

    // Reset input so the same file can be re-selected after a fix
    e.target.value = "";
  }

  // ── Submit ──────────────────────────────────────────────────────────────

  async function handleSubmit() {
    const validRows = parsedRows.filter((r) => !r.validationError);
    if (validRows.length === 0) return;

    setPhase("submitting");
    setBatchError(null);
    setRowCountWarning(null);
    setAllResults([]);

    // Build inputs
    const inputs: CreateUserInput[] = validRows.map((r) => ({
      email: r.email,
      displayName: r.displayName || undefined,
      roleCodes: r.roleCodes.length ? r.roleCodes : undefined,
      personaCodes: r.personaCodes.length ? r.personaCodes : undefined,
    }));

    // Chunk into ≤500-row batches
    const batches: CreateUserInput[][] = [];
    for (let i = 0; i < inputs.length; i += BATCH_SIZE) {
      batches.push(inputs.slice(i, i + BATCH_SIZE));
    }

    const accumulated: CreateUsersRowResult[] = [];
    const warnings: string[] = [];
    setBatchProgress({ done: 0, total: batches.length });

    for (let b = 0; b < batches.length; b++) {
      // Fix 6: clamp displayed batch number so it never shows "N+1 of N"
      setBatchProgress({ done: Math.min(b, batches.length - 1), total: batches.length });

      try {
        const { data } = await createUsers({
          variables: { users: batches[b] },
        });

        if (data?.createUsers) {
          // Fix 4: guard against BE returning a different row count
          if (data.createUsers.length !== batches[b].length) {
            const notice =
              `Batch ${b + 1}: expected ${batches[b].length} result(s) but received ` +
              `${data.createUsers.length} — some rows may be missing from the report below.`;
            console.warn("[CsvUploadModal]", notice);
            warnings.push(notice);
          }
          accumulated.push(...data.createUsers);
        }
      } catch (err) {
        // Fix 1: batch throw — preserve accumulated results, move to results
        // phase with a banner describing which batch failed. Do NOT revert to
        // preview; doing so would let the user re-submit already-created rows.
        const msg = err instanceof Error ? err.message : String(err);
        const banner =
          `Batch ${b + 1} of ${batches.length} failed: ${msg}`;
        setBatchError(banner);
        setAllResults(accumulated);
        setPhase("results");

        // Still fire onImported if any earlier batch succeeded
        if (accumulated.some((r) => r.success)) {
          onImported();
        }
        return;
      }
    }

    // All batches completed
    setBatchProgress({ done: batches.length, total: batches.length });
    if (warnings.length > 0) {
      setRowCountWarning(warnings.join(" | "));
    }
    setAllResults(accumulated);
    setPhase("results");

    if (accumulated.some((r) => r.success)) {
      onImported();
    }
  }

  // ── Derived state ───────────────────────────────────────────────────────

  const validCount = parsedRows.filter((r) => !r.validationError).length;
  const invalidCount = parsedRows.filter((r) => r.validationError).length;
  const isSubmitting = phase === "submitting";
  const canSubmit = phase === "preview" && validCount > 0;

  // ── Render ──────────────────────────────────────────────────────────────

  // Portal to <body> so the fixed overlay is a top-level sibling of #root and
  // escapes any ancestor that establishes a containing block / compositing
  // layer (transform, filter, contain) — which otherwise re-rasterizes and
  // visibly degrades the background text. (See SwitchPatientModal / #122.)
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Import users from CSV"
    >
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-lg font-bold">Import Users from CSV</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Bulk user creation via CSV upload
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-5 flex flex-col gap-5 min-h-0">

          {/* Format guidance + template download */}
          <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm flex flex-col gap-1.5">
            <p className="font-medium">Expected CSV format</p>
            <p className="text-muted-foreground text-xs font-mono">
              email, displayName, roleCodes, personaCodes
            </p>
            <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5 mt-0.5">
              <li><strong>email</strong> — required; one address per row; must be unique within the file</li>
              <li><strong>displayName</strong> — optional free text</li>
              <li>
                <strong>roleCodes</strong> — optional; multiple values separated by{" "}
                <code className="bg-muted px-1 rounded">{LIST_DELIMITER}</code>. Valid:{" "}
                <code className="bg-muted px-1 rounded">admin</code>,{" "}
                <code className="bg-muted px-1 rounded">clinical</code>,{" "}
                <code className="bg-muted px-1 rounded">readonly</code>
              </li>
              <li>
                <strong>personaCodes</strong> — optional; multiple values separated by{" "}
                <code className="bg-muted px-1 rounded">{LIST_DELIMITER}</code>. Valid:{" "}
                <code className="bg-muted px-1 rounded">PROVIDER</code>,{" "}
                <code className="bg-muted px-1 rounded">CUSTOMER_SERVICE</code>,{" "}
                <code className="bg-muted px-1 rounded">MSO</code>,{" "}
                <code className="bg-muted px-1 rounded">CARE_MANAGER</code>,{" "}
                <code className="bg-muted px-1 rounded">ADMIN</code>,{" "}
                <code className="bg-muted px-1 rounded">MEDICAL_ASSISTANT</code>
              </li>
            </ul>
            <button
              type="button"
              onClick={downloadTemplate}
              className="self-start mt-1 inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <Download className="w-3 h-3" aria-hidden="true" />
              Download template
            </button>
          </div>

          {/* File picker — hidden in results phase */}
          {phase !== "results" && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                id="csv-file-input"
                onChange={handleFileChange}
                aria-label="Select CSV file"
              />
              <label
                htmlFor="csv-file-input"
                className={
                  "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition-colors " +
                  "border-border hover:border-primary/50 hover:bg-primary/5"
                }
              >
                <Upload className="w-6 h-6 text-muted-foreground" aria-hidden="true" />
                <span className="text-sm font-medium">
                  {phase === "idle"
                    ? "Click to select a CSV file"
                    : "Click to select a different file"}
                </span>
                <span className="text-xs text-muted-foreground">.csv files only</span>
              </label>
            </div>
          )}

          {/* Parse error */}
          {parseError && (
            <div className="rounded-lg bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{parseError}</span>
            </div>
          )}

          {/* Row-count mismatch warning (non-fatal, results phase only) */}
          {rowCountWarning && (
            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 px-4 py-3 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{rowCountWarning}</span>
            </div>
          )}

          {/* Preview */}
          {phase === "preview" && parsedRows.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Preview</p>
              {invalidCount > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  {invalidCount} invalid {invalidCount === 1 ? "row" : "rows"} will be
                  skipped;{" "}{validCount} valid{" "}
                  {validCount === 1 ? "row" : "rows"} will be submitted.
                </p>
              )}
              {validCount > BATCH_SIZE && (
                <p className="text-xs text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded px-3 py-2 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  {validCount} rows will be submitted in{" "}
                  {Math.ceil(validCount / BATCH_SIZE)} sequential batches of up to{" "}
                  {BATCH_SIZE}.
                </p>
              )}
              <PreviewTable rows={parsedRows} />
            </div>
          )}

          {/* Submitting progress */}
          {phase === "submitting" && batchProgress && (
            <div className="flex flex-col items-center gap-3 py-6">
              <div
                className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin"
                aria-hidden="true"
              />
              {/* Fix 6: clamp displayed batch number — never shows "N+1 of N" */}
              <p className="text-sm text-muted-foreground">
                Submitting batch{" "}
                {Math.min(batchProgress.done + 1, batchProgress.total)} of{" "}
                {batchProgress.total}…
              </p>
            </div>
          )}

          {/* Results */}
          {phase === "results" && (
            <div>
              <p className="text-sm font-semibold mb-2">Import Results</p>
              {allResults.length > 0 ? (
                <ResultsTable results={allResults} batchError={batchError} />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No rows were processed before the failure.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-border shrink-0">
          {phase === "results" ? (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!canSubmit || isSubmitting}
                onClick={() => { void handleSubmit(); }}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors"
              >
                {isSubmitting
                  ? "Importing…"
                  : `Import ${validCount > 0 ? validCount : ""} User${validCount !== 1 ? "s" : ""}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
